import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { type EnvelopeStatus, envelopesApi, type EnvelopeView } from '@/api/envelopes.api';
import { foldersApi } from '@/api/folders.api';
import { SequentialChain } from '@/components/envelope/SequentialChain';
import { FileIcon, PlusIcon, SearchIcon } from '@/components/icons/Icons';
import { formatBytes, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';

// Tab strip mirrors the hero tiles' vocabulary: what is waiting on ME vs. on
// SOMEONE ELSE, then the terminal buckets. "sent" and "in_progress" are one
// bucket here — both mean "out for signature, not my turn" and they rendered
// as two identical "In progress" tabs before.
const TAB_KEYS: Array<{ key: string; status?: string }> = [
  { key: 'all' },
  { key: 'pending_mine' },
  { key: 'pending_others', status: 'sent,in_progress' },
  { key: 'draft',          status: 'draft' },
  { key: 'declined',       status: 'declined' },
  { key: 'completed',      status: 'completed' },
  { key: 'expired',        status: 'expired' },
];

// The former standalone "Documents" (envelopes list) page — now embedded
// directly inside the Dashboard. Renders without its own AppShell wrapper.
export function EnvelopesSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Honour ?tab= so the old /inbox redirect lands on "Awaiting my signature".
  const [activeTab, setActiveTab] = useState<string>(() => searchParams.get('tab') ?? 'all');
  const [search, setSearch] = useState('');
  // Initialise from ?folder= so dashboard links land on the right view.
  const [activeFolder, setActiveFolder] = useState<string | undefined>(
    () => searchParams.get('folder') ?? undefined,
  );
  const [creatingFolder, setCreatingFolder] = useState(false);
  // Date-range filter (by last-updated day) — applied client-side.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Folders are an admin-only organisational tool; hide the whole folders UI
  // (sidebar + table column) from non-admins.
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === 'owner' || me?.role === 'admin';

  // Mirror folder selection in the URL so the view is shareable / bookmarkable.
  useEffect(() => {
    if (activeFolder) {
      if (searchParams.get('folder') !== activeFolder) {
        setSearchParams({ folder: activeFolder }, { replace: true });
      }
    } else if (searchParams.has('folder')) {
      const next = new URLSearchParams(searchParams);
      next.delete('folder');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolder]);

  const status = TAB_KEYS.find((tab) => tab.key === activeTab)?.status;
  const tabLabel = (key: string): string => {
    if (key === 'all') return t('envelopes_list.filter_all');
    if (key === 'pending_mine') return t('envelopes_list.tab_pending_mine');
    if (key === 'pending_others') return t('envelopes_list.tab_pending_others');
    return t(`envelope.status_${key}`);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['envelopes', 'list', activeTab, search, activeFolder],
    queryFn: () =>
      envelopesApi.list({
        status,
        search: search || undefined,
        folder: activeFolder,
        perPage: 50,
      }),
  });

  // Client-side date filter (by last-updated day) on the current page of rows.
  const visibleItems = useMemo(() => {
    const list = data?.items ?? [];
    if (!dateFrom && !dateTo) return list;
    return list.filter((e) => {
      const day = e.updatedAt.slice(0, 10); // YYYY-MM-DD
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [data, dateFrom, dateTo]);

  const { data: folderData } = useQuery({
    queryKey: ['folders', 'list'],
    queryFn: () => foldersApi.list(),
  });

  const createFolderMutation = useMutation({
    mutationFn: (name: string) => foldersApi.create({ name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders', 'list'] });
      setCreatingFolder(false);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (uuid: string) => foldersApi.delete(uuid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders', 'list'] });
      qc.invalidateQueries({ queryKey: ['envelopes', 'list'] });
      if (activeFolder && !folderData?.folders.some((f) => f.id === activeFolder)) {
        setActiveFolder(undefined);
      }
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const moveMutation = useMutation({
    mutationFn: ({ envelopeId, folderId }: { envelopeId: string; folderId: string | null }) =>
      foldersApi.moveEnvelope(envelopeId, folderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'list'] });
      qc.invalidateQueries({ queryKey: ['folders', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const counts = useTabCounts();

  // Documents awaiting my signature — folds the old "Documents for sign" inbox
  // into this list as a dedicated tab.
  const { data: awaitingMine = [], isLoading: awaitingLoading } = useQuery({
    queryKey: ['envelopes', 'inbox'],
    queryFn: () => envelopesApi.inbox(),
  });
  const signMutation = useMutation({
    mutationFn: (uuid: string) => envelopesApi.takeSignLink(uuid),
    onSuccess: ({ token }) => navigate(`/sign/${token}`),
    onError: (err) => toast.error(formatApiMessage(err)),
  });
  const isAwaiting = activeTab === 'pending_mine';

  return (
    <section data-tour="documents">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t('envelopes_list.title')}</h1>
          <p className="page-subtitle">{t('envelopes_list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              envelopesApi
                .exportXlsx({ status, search: search || undefined, folder: activeFolder })
                .catch((err) => toast.error(formatApiMessage(err)))
            }
            className="btn btn-secondary"
            title={t('envelopes_list.export_xlsx_hint')}
          >
            ⬇ Excel
          </button>
          <button
            onClick={() =>
              envelopesApi
                .exportCsv({ status, search: search || undefined, folder: activeFolder })
                .catch((err) => toast.error(formatApiMessage(err)))
            }
            className="btn btn-secondary"
            title={t('envelopes_list.export_csv_hint')}
          >
            ⬇ CSV
          </button>
          <Link to="/envelopes/new" className="btn btn-primary">
            <PlusIcon className="w-3.5 h-3.5" />
            {t('envelopes_list.new_envelope_btn')}
          </Link>
        </div>
      </div>

      <nav className="tabs">
        {TAB_KEYS.map((tab) => {
          const active = activeTab === tab.key;
          const count =
            tab.key === 'pending_mine'
              ? awaitingMine.length
              : counts[tab.key as keyof typeof counts];
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn('tab', active && 'tab-active')}
            >
              {tabLabel(tab.key)}
              {count !== undefined && count > 0 && (
                <span className="badge badge-muted ml-1.5">{count}</span>
              )}
            </button>
          );
        })}
      </nav>

      {isAwaiting ? (
        <AwaitingMeTable
          rows={awaitingMine}
          loading={awaitingLoading}
          onSign={(id) => signMutation.mutate(id)}
          signing={signMutation.isPending}
        />
      ) : (
      <>
      <div className="card !p-3.5 mb-4 flex items-center gap-2.5 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <SearchIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            className="input pl-9 bg-bg border-transparent focus:bg-white"
            placeholder={t('envelopes_list.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            className="input bg-bg border-transparent focus:bg-white !py-2"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            title="From date"
          />
          <span className="text-muted">–</span>
          <input
            type="date"
            className="input bg-bg border-transparent focus:bg-white !py-2"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            title="To date"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              className="text-[18px] text-muted hover:text-danger leading-none px-1"
              title="Clear dates"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className={cn('grid grid-cols-1 gap-4', isAdmin && 'lg:grid-cols-[220px_1fr]')}>
        {/* Folders sidebar — admin only */}
        {isAdmin && (
        <aside className="card !p-2 self-start">
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="text-[10px] uppercase font-semibold text-muted tracking-wider">
              {t('envelopes_list.folders_title')}
            </div>
            <button
              onClick={() => setCreatingFolder((v) => !v)}
              className="text-[16px] text-muted hover:text-primary leading-none"
              title={t('envelopes_list.new_folder')}
            >
              +
            </button>
          </div>
          <ul className="space-y-0.5 mt-1">
            <FolderRow
              label={t('envelopes_list.folder_all')}
              count={folderData?.folders.reduce((s, f) => s + f.count, 0) ?? 0}
              active={activeFolder === undefined}
              onClick={() => setActiveFolder(undefined)}
              color="#9CA3AF"
            />
            <FolderRow
              label={t('envelopes_list.folder_none')}
              count={folderData?.unfiledCount ?? 0}
              active={activeFolder === 'unfiled'}
              onClick={() => setActiveFolder('unfiled')}
              color="#D1D5DB"
            />
            {folderData?.folders.map((f) => (
              <FolderRow
                key={f.id}
                label={f.name}
                count={f.count}
                active={activeFolder === f.id}
                onClick={() => setActiveFolder(f.id)}
                color={f.color}
                onDelete={() => {
                  if (confirm(t('envelopes_list.delete_folder_confirm', { name: f.name }))) {
                    deleteFolderMutation.mutate(f.id);
                  }
                }}
              />
            ))}
          </ul>
          {creatingFolder && (
            <NewFolderInline
              onCreate={(name) => createFolderMutation.mutate(name)}
              onCancel={() => setCreatingFolder(false)}
              pending={createFolderMutation.isPending}
            />
          )}
        </aside>
        )}

        <div className="card !p-0">
          <table className="table">
            <thead>
              <tr>
                <th>{t('envelopes_list.th_title')}</th>
                <th>{t('envelopes_list.th_recipients')}</th>
                <th>{t('envelopes_list.th_status')}</th>
                <th>{t('envelopes_list.filter_folder')}</th>
                <th>{t('common.updated')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="!px-4 !py-10 text-center text-muted text-[13px]">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!isLoading && visibleItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="!px-4 !py-12 text-center">
                    <FileIcon className="w-7 h-7 mx-auto text-muted mb-2" />
                    <div className="font-medium text-gray-900 text-[14px]">{t('envelopes_list.no_envelopes_title')}</div>
                    <div className="text-[13px] text-muted mt-1">
                      {activeFolder
                        ? t('envelopes_list.no_envelopes_filter')
                        : t('envelopes_list.no_envelopes_text')}
                    </div>
                    <Link to="/envelopes/new" className="btn btn-primary mt-4 inline-flex">
                      <PlusIcon className="w-3.5 h-3.5" />
                      {t('envelopes_list.new_envelope_btn')}
                    </Link>
                  </td>
                </tr>
              )}
              {visibleItems.map((env) => (
                <Row
                  key={env.id}
                  env={env}
                  showFolder={true}
                  folders={folderData?.folders ?? []}
                  onMove={(folderId) =>
                    moveMutation.mutate({ envelopeId: env.id, folderId })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {visibleItems.length > 0 && (
        <div className="mt-3 text-[12px] text-muted text-right">
          {t('common.page')} 1 — {visibleItems.length} / {data?.total ?? visibleItems.length}
        </div>
      )}
      </>
      )}
    </section>
  );
}

function useTabCounts() {
  return {
    all: useQuery({
      queryKey: ['envelopes', 'count', 'all'],
      queryFn: () => envelopesApi.list({ perPage: 1 }),
    }).data?.total,
    draft: useQuery({
      queryKey: ['envelopes', 'count', 'draft'],
      queryFn: () => envelopesApi.list({ status: 'draft', perPage: 1 }),
    }).data?.total,
    // One bucket for "out for signature, not my turn" — the API takes the
    // two statuses as a comma-separated set so this stays a single count.
    pending_others: useQuery({
      queryKey: ['envelopes', 'count', 'pending_others'],
      queryFn: () => envelopesApi.list({ status: 'sent,in_progress', perPage: 1 }),
    }).data?.total,
    completed: useQuery({
      queryKey: ['envelopes', 'count', 'completed'],
      queryFn: () => envelopesApi.list({ status: 'completed', perPage: 1 }),
    }).data?.total,
    declined: useQuery({
      queryKey: ['envelopes', 'count', 'declined'],
      queryFn: () => envelopesApi.list({ status: 'declined', perPage: 1 }),
    }).data?.total,
    expired: useQuery({
      queryKey: ['envelopes', 'count', 'expired'],
      queryFn: () => envelopesApi.list({ status: 'expired', perPage: 1 }),
    }).data?.total,
  };
}

function Row({
  env,
  folders,
  onMove,
  showFolder,
}: {
  env: EnvelopeView;
  folders: Array<{ id: string; name: string; color: string }>;
  onMove: (folderId: string | null) => void;
  showFolder: boolean;
}) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const canManageFolder =
    !!me &&
    (me.id === env.creator.id || me.role === 'owner' || me.role === 'admin');
  const totalBytes = env.documents.reduce((s, d) => s + Number(d.fileSize), 0);
  const totalPages = env.documents.reduce((s, d) => s + d.pagesCount, 0);
  return (
    <tr>
      <td>
        <Link
          to={`/envelopes/${env.id}`}
          className="flex items-center gap-2.5 no-underline text-inherit"
        >
          <div className="w-8 h-8 rounded-md bg-primary-50 text-primary flex items-center justify-center shrink-0">
            <FileIcon />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate hover:text-primary">{env.title}</div>
            {env.documentNumber && (
              <code className="text-[10px] text-gray-700 bg-gray-100 px-1 rounded">
                {env.documentNumber}
              </code>
            )}
            <div className="text-[12px] text-muted">
              {formatBytes(totalBytes)} · {totalPages} {t('documents_step.pages')}
            </div>
          </div>
        </Link>
      </td>
      <td>
        {env.recipients.length === 0 ? (
          <span className="text-muted text-[11px]">—</span>
        ) : (
          <SequentialChain
            recipients={env.recipients}
            workflowType={env.workflowType}
            envelopeStatus={env.status}
            variant="compact"
          />
        )}
      </td>
      <td>
        <StatusPill status={env.status as EnvelopeStatus} />
      </td>
      {showFolder && (
      <td>
        {canManageFolder ? (
          <select
            className="text-[12px] bg-transparent border border-border rounded-md px-1.5 py-1 cursor-pointer hover:bg-gray-50 max-w-[140px]"
            value={env.folder?.id ?? ''}
            onChange={(e) => onMove(e.target.value || null)}
            title={env.folder ? env.folder.name : t('envelopes_list.folder_none')}
          >
            <option value="">— {t('envelopes_list.folder_none')} —</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        ) : (
          <span
            className="text-[12px] text-gray-700 inline-flex items-center gap-1"
            title={t('envelopes_list.folder_readonly_hint') as string}
          >
            {env.folder ? (
              <>
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: env.folder.color }}
                />
                {env.folder.name}
              </>
            ) : (
              <span className="text-muted">—</span>
            )}
          </span>
        )}
      </td>
      )}
      <td className="text-muted text-[13px]">{shortDate(env.updatedAt)}</td>
      <td className="text-right whitespace-nowrap">
        {env.status === 'draft' && canManageFolder && (
          <Link to={`/envelopes/new?continue=${env.id}`} className="btn btn-ghost btn-sm text-primary">
            {t('common.edit')}
          </Link>
        )}
        <Link to={`/envelopes/${env.id}`} className="btn btn-ghost btn-sm">{t('common.view')}</Link>
      </td>
    </tr>
  );
}

function FolderRow({
  label,
  count,
  active,
  onClick,
  onDelete,
  color,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  onDelete?: () => void;
  color: string;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center group">
      <button
        onClick={onClick}
        className={cn(
          'flex-1 flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-left transition-colors',
          active ? 'bg-primary-50 text-primary font-medium' : 'text-gray-700 hover:bg-gray-100',
        )}
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        <span className="truncate flex-1">{label}</span>
        <span className="text-[10px] text-muted">{count}</span>
      </button>
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-0 group-hover:opacity-100 text-[14px] text-muted hover:text-danger px-1.5"
          title={t('common.delete')}
        >
          ×
        </button>
      )}
    </li>
  );
}

function NewFolderInline({
  onCreate,
  onCancel,
  pending,
}: {
  onCreate: (name: string) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onCreate(name.trim());
      }}
      className="px-1 pt-2 border-t border-border mt-2 flex gap-1"
    >
      <input
        autoFocus
        placeholder={t('envelopes_list.folder_name_prompt')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="flex-1 text-[12px] px-2 py-1 border border-border rounded-md outline-none focus:border-primary"
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
      />
      <button
        type="submit"
        disabled={!name.trim() || pending}
        className="text-[12px] text-primary px-2"
      >
        {pending ? '…' : '✓'}
      </button>
    </form>
  );
}

// "Awaiting my signature" — the former Inbox ("Documents for sign"), folded in
// as a Documents tab. Lists envelopes that need the current user's signature.
function AwaitingMeTable({
  rows,
  loading,
  onSign,
  signing,
}: {
  rows: EnvelopeView[];
  loading: boolean;
  onSign: (uuid: string) => void;
  signing: boolean;
}) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  return (
    <div className="card !p-0">
      {loading ? (
        <div className="text-center py-12 text-muted text-[13px]">{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-4xl mb-2">📭</div>
          <div className="font-medium text-gray-900">{t('inbox.empty_title')}</div>
          <p className="text-[13px] text-muted mt-1 max-w-md mx-auto">{t('inbox.empty_text')}</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('inbox.th_envelope')}</th>
              <th>{t('inbox.th_from')}</th>
              <th>{t('inbox.th_status')}</th>
              <th>{t('inbox.th_my_status')}</th>
              <th>{t('inbox.th_sent')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((env) => {
              const myRecipient = env.recipients.find(
                (r) => r.email.toLowerCase() === me?.email.toLowerCase(),
              );
              const myStatus = myRecipient?.status ?? 'pending';
              const canSign = myStatus !== 'signed' && myStatus !== 'declined';
              return (
                <tr key={env.id}>
                  <td>
                    <div className="font-medium text-gray-900">{env.title}</div>
                    {env.message && (
                      <div className="text-[12px] text-muted truncate max-w-[300px]">{env.message}</div>
                    )}
                  </td>
                  <td className="text-[13px] text-gray-700">
                    {env.creator.firstName} {env.creator.lastName}
                    <div className="text-[11px] text-muted">{env.creator.email}</div>
                  </td>
                  <td><StatusPill status={env.status as EnvelopeStatus} /></td>
                  <td>
                    <span
                      className={`badge text-[10px] ${
                        myStatus === 'signed'
                          ? 'badge-success'
                          : myStatus === 'declined'
                            ? 'badge-danger'
                            : myStatus === 'viewed'
                              ? 'badge-warning'
                              : 'badge-muted'
                      }`}
                    >
                      {t(`envelope.recipient_status_${myStatus}`)}
                    </span>
                  </td>
                  <td className="text-[12px] text-muted">
                    {env.sentAt ? formatDateTime(env.sentAt) : '—'}
                  </td>
                  <td className="text-right">
                    {canSign ? (
                      <button
                        onClick={() => onSign(env.id)}
                        disabled={signing}
                        className="btn btn-primary btn-sm"
                      >
                        {signing ? t('inbox.opening') : t('inbox.open_to_sign')}
                      </button>
                    ) : (
                      <Link to={`/envelopes/${env.id}`} className="btn btn-secondary btn-sm">
                        {t('common.view')}
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: EnvelopeStatus }) {
  const { t } = useTranslation();
  const cls: Record<EnvelopeStatus, string> = {
    draft:       'badge-muted',
    sent:        'badge-info',
    in_progress: 'badge-warning',
    completed:   'badge-success',
    declined:    'badge-danger',
    expired:     'badge-muted',
    voided:      'badge-muted',
  };
  return <span className={`badge ${cls[status]}`}>{t(`envelope.status_${status}`)}</span>;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
